<!-- =========================================================================================
    File Name: Position.vue
    Description: Page to change the placement xyz position in space
========================================================================================== -->

<template>
  <div>
    <div id="scene3d">
      <img
        id="iPhoneUi"
        v-if="isFirstPersonView"
        :src="require('../../../../../assets/images/hoverlay/iPhone-Ui.png')"
        :height="CANVAS_HEIGHT - 50"
        :width="CANVAS_HEIGHT / 2"
      />
      <!-- 1st-Person View Switch  -->
      <center>
        <div class="povBottomRight">
          <vx-tooltip
            color="#7c53e6"
            text="Simulate what users would see when viewing this space through a typical smartphone in the Hoverlay app."
          >
            First-Person View
          </vx-tooltip>
          <vs-switch v-model="isFirstPersonView">
            <span slot="on">On</span>
            <span slot="off">Off</span>
          </vs-switch>
        </div>
      </center>
      <center v-if="controlModeFloor" class="mt-2 center-top-left">
        <ul>
          <li>
            <vs-radio v-model="controlModeFloor" vs-value="translate">Move</vs-radio>
          </li>
          <li>
            <vs-radio v-model="controlModeFloor" vs-value="rotate">Rotate</vs-radio>
          </li>
          <li>
            <vs-radio v-model="controlModeFloor" vs-value="scale">Scale</vs-radio>
          </li>
        </ul>
      </center>
      <!-- <div v-if="this.renderer">{{ this.renderer.info }}</div> -->
      <div v-if="isLoading" class="center-top-right-loading">
        <div class="contained-example-container">
          <div id="div-with-loading" class="vs-con-loading__container"></div>
        </div>
      </div>
      <center
        v-if="selectedNode && selectedNode.pid && this.transformDictionnary[selectedNode.pid]"
        class="center-top-right"
      >
        <div v-if="controlModeFloor == 'translate'">
          x: {{ this.transformDictionnary[selectedNode.pid].position.x.toFixed(2) }}m &nbsp; y:
          {{ this.transformDictionnary[selectedNode.pid].position.y.toFixed(2) }}m &nbsp; z:
          {{ -this.transformDictionnary[selectedNode.pid].position.z.toFixed(2) }}m
          <br />
        </div>
        <div v-if="controlModeFloor == 'rotate'">
          x: {{ radToDeg(-this.transformDictionnary[selectedNode.pid].rotation.x).toFixed(2) }}° &nbsp; y:
          {{ radToDeg(-this.transformDictionnary[selectedNode.pid].rotation.y).toFixed(2) }}° &nbsp; z:
          {{ radToDeg(this.transformDictionnary[selectedNode.pid].rotation.z).toFixed(2) }}°
          <br />
        </div>
        <div v-if="controlModeFloor == 'scale'">
          {{ parseInt(this.transformDictionnary[selectedNode.pid].scale.x * 100) }}%
        </div>
      </center>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { TWEEN } from 'three/examples/jsm/libs/tween.module.min'

import * as HoverlayThreeUtils from '@/assets/js/utils/hoverlay-three-utils.js'

import * as HoverlayUtils from '@/assets/js/utils/hoverlay-utils.js'
import * as DestroyUtils from '@/assets/js/utils/destroy-utils.js'

import 'video.js/dist/video-js.css'
import 'flatpickr/dist/flatpickr.css'

export default {
  components: {},
  props: {
    selectedNode: null,
    space: {},
    nodes: {},
    anchors: {},
  },
  data() {
    return {
      // Three js variables
      cameraPersp: null,
      currentCamera: null,
      scene: null,
      meshes: [],
      renderer: null,
      controlModeFloor: null,
      transformControlDictionnary: [],
      transformDictionnary: [],
      mixerDictionnary: [],
      transformControls: null,
      orbit: null,
      CANVAS_HEIGHT: null,
      CANVAS_WIDTH: null,
      mannequinObject: null,
      isFirstPersonView: false,
      thirdPersonCoordinates: null,
      thirdPersonOrbitTarget: null,
      thirdPersonAngleX: null,
      thirdPersonAngleY: null,
      thirdPersonAngleZ: null,
      // Raycasting
      raycaster: null,
      mouse: null,
      threeJSVideoComponents: [],
      clock: null,
      requestAnimationFrameId: null,
      dracoLoader: null,
      gltfLoader: null,
      isLoading: true,
      // Keep a references on created texture in order for them to be deleted
      // History CTRL-Z CTRL-Y Undo/Repeat
      historyTransformArray: [],
      historyTransformArrayIndex: 0,
      // Thumbnail
      captureThumbnail: false,
      thumbnail: null,
    }
  },
  watch: {
    //swaps between first-person view and third-person view
    isFirstPersonView: function() {
      //if switch is ON
      if (this.isFirstPersonView) {
        //go into 3rd Person view
        //copy data of 3rd-Person Camera for else case
        this.thirdPersonCoordinates = JSON.parse(JSON.stringify(this.currentCamera.position))
        this.thirdPersonAngleX = JSON.parse(JSON.stringify(this.currentCamera.rotation.x))
        this.thirdPersonAngleY = JSON.parse(JSON.stringify(this.currentCamera.rotation.y))
        this.thirdPersonAngleZ = JSON.parse(JSON.stringify(this.currentCamera.rotation.z))
        this.thirdPersonOrbitTarget = JSON.parse(JSON.stringify(this.orbit.target))

        const coords = {
          x: this.currentCamera.position.x,
          y: this.currentCamera.position.y,
          z: this.currentCamera.position.z,
        }
        const angles = {
          x: this.currentCamera.rotation.x,
          y: this.currentCamera.rotation.y,
          z: this.currentCamera.rotation.z,
        }

        //animation for camera position
        new TWEEN.Tween(coords)
          .to({ x: 0, y: 1.3, z: 0 }, 750)
          .easing(TWEEN.Easing.Back.In)
          .onUpdate(() => this.currentCamera.position.set(coords.x, coords.y, coords.z))
          .start()

        //animation for camera angle
        new TWEEN.Tween(angles)
          .to({ x: 0, y: 0, z: 0 }, 750)
          .onUpdate(() => this.currentCamera.rotation.set(angles.x, angles.y, angles.z))
          .start()

        //setup for 1st-Person View
        this.orbit.minDistance = 0
        this.orbit.enableZoom = false
        this.orbit.enablePan = false
        this.orbit.maxDistance = 0.01
        this.orbit.maxPolarAngle = Math.PI
        this.orbit.target = new THREE.Vector3(0, 1.3, -0.01) //position that camera is centered at
        this.mannequinObject.traverse(function(object) {
          object.visible = false
        }, 700) //should delay when mannequin disappears but doesn't work,
        this.currentCamera.fov = 75
        this.currentCamera.updateProjectionMatrix()
      }
      //if switch is OFF
      else {
        //go into 3rd-Person View

        //setup for tween
        const coords = {
          x: this.currentCamera.position.x,
          y: this.currentCamera.position.y,
          z: this.currentCamera.position.z,
        }
        const angles = {
          x: this.currentCamera.rotation.x,
          y: this.currentCamera.rotation.y,
          z: this.currentCamera.rotation.z,
        }

        //animation for camera position
        new TWEEN.Tween(coords)
          .to(
            { x: this.thirdPersonCoordinates.x, y: this.thirdPersonCoordinates.y, z: this.thirdPersonCoordinates.z },
            750
          )
          .easing(TWEEN.Easing.Back.In)
          .onUpdate(() => this.currentCamera.position.set(coords.x, coords.y, coords.z))
          .start()

        //animation for camera angle
        new TWEEN.Tween(angles)
          .to({ x: this.thirdPersonAngleX, y: this.thirdPersonAngleY, z: this.thirdPersonAngleZ }, 750)
          .onUpdate(() => this.currentCamera.rotation.set(angles.x, angles.y, angles.z))
          .start()

        //Set-up for going back to 3rd-Person View
        this.orbit.maxPolarAngle = Math.PI / 2 // prevent the camera from going under the ground
        this.orbit.minDistance = 1 // the minimum distance the camera must have from center
        this.orbit.maxDistance = 100 // the maximum distance the camera must have from center
        this.orbit.enableZoom = true
        this.orbit.enablePan = true
        this.orbit.target = new THREE.Vector3(
          this.thirdPersonOrbitTarget.x,
          this.thirdPersonOrbitTarget.y,
          this.thirdPersonOrbitTarget.z
        )
        setTimeout(
          this.mannequinObject.traverse(function(object) {
            object.visible = true
          }),
          700
        )
        this.currentCamera.fov = 50
        this.currentCamera.updateProjectionMatrix()
      }
    },
    historyTransformArray: function() {
      this.historyTransformArrayIndex = this.historyTransformArray.length - 1
    },
    controlModeFloor: function(mode) {
      this.changeTransformControl(mode)
    },

    selectedNode: function(selectedNode) {
      // console.log(selectedNode)
      if (selectedNode && selectedNode.pid && this.transformDictionnary[selectedNode.pid]) {
        // Detached controls
        if (this.transformControls != null) {
          this.transformControls.detach() // <- THIS IS CORRECT
          this.unregisterTransformControl()
          this.transformControls.dispose()
          this.scene.remove(this.transformControls)

          delete this.transformControlDictionnary[selectedNode.pid]
        }
        // Add controls on the selected object
        this.addTransformControls(this.transformDictionnary[selectedNode.pid], selectedNode)
        // If a video is found unmuted it
        var video = document.getElementById(`video_${selectedNode.pid}`)
        if (video) video.muted = false
      }
    },
    nodes: function(newNodes, oldNodes) {
      // Indentify nodes that have been ADDED and add them on the threeJS scene
      var onlynewNewNodes = newNodes.filter(HoverlayUtils.spacesComparer(oldNodes))
      for (const node of onlynewNewNodes) {
        this.createHobjectRepresentation(node)
      }

      // Indentify nodes that have been REMOVED and remove them from the threeJS scene
      // Detach transform first and then remove object from the scene
      var onlyInOldNodes = oldNodes.filter(HoverlayUtils.spacesComparer(newNodes))
      onlyInOldNodes.forEach(node => {
        this.transformControls.detach(this.transformDictionnary[node.pid])
        this.transformControls.dispose()
        this.transformControls = null
        if (this.transformDictionnary[node.pid].parent)
          this.transformDictionnary[node.pid].parent.remove(this.transformDictionnary[node.pid])
        else this.scene.remove(this.transformDictionnary[node.pid])
        this.meshes = this.meshes.filter(mesh => mesh.name !== node.pid)
        delete this.mixerDictionnary[node.pid]
        DestroyUtils.disposeHierarchy(
          {
            node: this.transformDictionnary[node.pid],
          },
          DestroyUtils.disposeNode
        )
        this.transformDictionnary[node.pid] = null
        delete this.transformDictionnary[node.pid]
        var selectedObject = this.scene.getObjectByName(node.pid)
        this.scene.remove(selectedObject)

        this.render()
      })
    },
  },
  // https://sketchfab.com/3d-models/wooden-artist-mannequin-be47b21c2e8f4a0f975e89a9c50a4aa5
  mounted() {
    this.$vs.loading({
      container: '#div-with-loading',
      scale: 0.6,
    })

    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()
    this.clock = new THREE.Clock()

    // Adjust 3D scene height
    var win = window,
      doc = document,
      docElem = doc.documentElement,
      body = doc.getElementsByTagName('body')[0],
      // x = win.innerWidth || docElem.clientWidth || body.clientWidth,
      y = win.innerHeight || docElem.clientHeight || body.clientHeight
    // document.getElementById('scene3d').style.height = `${y * (72 / 100)}px`

    document.getElementById('scene3d').style.height = `${y * (72 / 100)}px`

    // Optional: Provide a DRACOLoader instance to decode compressed mesh data
    this.gltfLoader = new GLTFLoader()
    this.dracoLoader = new DRACOLoader()
    this.dracoLoader.setDecoderPath(`${window.location.origin}/js/draco/`)
    this.gltfLoader.setDRACOLoader(this.dracoLoader)

    this.$nextTick(function() {
      // Code that will run only after the
      // entire view has been rendered
      this.setupScene()

      this.addMannequin()
      if (this.space.settings && this.space.settings.compass_oriented) this.addCompassImage()

      this.render()
      this.controlModeFloor = 'translate'
    })
  },
  created() {
    this.$eventBus.$on('nodePositionChanged', this.onNodePositionChanged)
    this.$eventBus.$on('onSpaceSaved', this.onSpaceSaved)
  },
  beforeDestroy() {
    this.$eventBus.$off('hobjectChanged')
    this.$eventBus.$off('hobjectChanged')

    this.disposeScene()

    //    for (const video of this.threeJSVideoComponents) {
    //   video = null;
    // }
  },
  methods: {
    // function triggered when user change the input transform values
    onNodePositionChanged(node) {
      var transform = this.transformDictionnary[node.pid]
      if (transform == null) return
      // change position if needed
      if (transform.position.x != node.x) transform.position.x = node.x
      if (transform.position.y != node.y) transform.position.y = node.y
      if (transform.position.z != -node.z) transform.position.z = -node.z
      // change scale if needed
      if (transform.scale.x != node.x || transform.scale.y != node.y || transform.scale.z != node.z) {
        transform.scale.z = node.scale
        transform.scale.x = node.scale
        transform.scale.y = node.scale
      }
      // change rotation if needed
      var transform_angle_x = parseInt(THREE.MathUtils.radToDeg(-transform.rotation.x))
      if (transform_angle_x != node.angle_x) {
        transform.rotation.x = THREE.MathUtils.degToRad(-node.angle_x)
      }
      var transform_angle_y = parseInt(THREE.MathUtils.radToDeg(-transform.rotation.y))
      if (transform_angle_y != node.angle_y) {
        transform.rotation.y = THREE.MathUtils.degToRad(-node.angle_y)
      }
      var transform_angle_z = parseInt(THREE.MathUtils.radToDeg(-transform.rotation.z))
      if (transform_angle_z != node.angle_z) {
        transform.rotation.z = THREE.MathUtils.degToRad(-node.angle_z)
      }
      this.mouseUpHandler(transform, node, null)
    },
    onSpaceSaved() {
      this.captureThumbnail = true
    },

    async setupScene() {
      var scene3d = document.getElementById('scene3d')

      // if (scene3d.firstChild) {
      //   scene3d.firstChild.remove()
      // }

      this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
      this.renderer.shadowMap.enabled = true
      this.renderer.setPixelRatio(window.devicePixelRatio)

      this.CANVAS_HEIGHT = document.getElementById('scene3d').offsetHeight
      this.CANVAS_WIDTH = document.getElementById('scene3d').offsetWidth

      this.renderer.setSize(this.CANVAS_WIDTH, this.CANVAS_HEIGHT)
      scene3d.appendChild(this.renderer.domElement)

      const aspect = this.CANVAS_WIDTH / this.CANVAS_HEIGHT

      this.cameraPersp = new THREE.PerspectiveCamera(50, aspect, 0.01, 30000)
      this.currentCamera = this.cameraPersp
      this.currentCamera.position.set(-2, 4, 6)

      this.scene = new THREE.Scene()
      const color = 0xffffff
      const density = 0.01
      this.scene.fog = new THREE.FogExp2(color, density)

      var gridHelper = new THREE.GridHelper(1000, 1000, 0x878787, 0xcdcdcd)
      // gridHelper = gridHelper.material.transparent = true
      // gridHelper.material.opacity = 0.9

      this.scene.add(gridHelper)

      const geometry = new THREE.PlaneGeometry(10000, 1000, 32)
      const material = new THREE.ShadowMaterial({ opacity: 0.2 })
      const plane = new THREE.Mesh(geometry, material)
      plane.receiveShadow = true

      plane.rotateX(-Math.PI / 2)

      this.scene.add(plane)

      const axesHelper = new THREE.AxesHelper(100)
      axesHelper.size = 3
      axesHelper.setColors(new THREE.Color(0x007500), new THREE.Color(0xff0000), new THREE.Color(0x0000ff))
      this.scene.add(axesHelper)

      const light = new THREE.AmbientLight(0x858585) // soft white light
      this.scene.add(light)

      const hemiLight = new THREE.HemisphereLight(0xcccccc, 0x111111)
      hemiLight.position.set(0, 20, -10)
      this.scene.add(hemiLight)

      const dirLight = new THREE.DirectionalLight(0xcccccc)
      dirLight.position.set(-5, 20, +10)
      dirLight.castShadow = true
      dirLight.shadow.camera.top = 100
      dirLight.shadow.camera.bottom = -100
      dirLight.shadow.camera.left = -100
      dirLight.shadow.camera.right = 100
      dirLight.shadow.camera.near = 0.1
      dirLight.shadow.camera.far = 500
      dirLight.shadow.mapSize.width = 5120
      dirLight.shadow.mapSize.height = 5120
      this.scene.add(dirLight)

      this.orbit = new OrbitControls(this.currentCamera, this.renderer.domElement)
      this.orbit.target = new THREE.Vector3(0, 1.3, 0) // set the center
      this.orbit.maxPolarAngle = Math.PI / 2 // prevent the camera from going under the ground
      this.orbit.minDistance = 1 // the minimum distance the camera must have from center
      this.orbit.maxDistance = 100000 // the maximum distance the camera must have from center
      this.orbit.update()
      this.orbit.addEventListener('change', this.render)

      HoverlayThreeUtils.loadReflectionProbe(this)
      await this.createHobjectsRepresentation()
      this.animate()
      this.isLoading = false
      window.addEventListener('resize', this.onWindowResize)
      // window.addEventListener('mousemove', this.onMouseMove, false)
      window.addEventListener('keydown', this.onKeyDown)
      window.addEventListener('keyup', this.onKeyUp)
    },
    async createHobjectRepresentation(node) {
      const hobject = this.getHobject(node.hobject_pid)
      const modelGroup = new THREE.Object3D()
      var model

      switch (hobject.model_identifier) {
        case 'hoverlay.core.Audio.1.0':
          model = await HoverlayThreeUtils.createCoreAudio(hobject, this.renderer, this.render)
          this.createMeshArray(node, model)
          modelGroup.add(model)
          break
        case 'hoverlay.core.UnityAssetBundle.1.0':
          model = await HoverlayThreeUtils.createCoreImage(hobject, this.renderer, this.render)
          this.createMeshArray(node, model)
          modelGroup.add(model)
          break
        case 'hoverlay.core.Button.1.0':
          model = await HoverlayThreeUtils.createCoreButton(hobject, modelGroup, this.gltfLoader)
          this.createMeshArray(node, model)
          modelGroup.add(model)
          break
        case 'hoverlay.core.TextCard.1.0':
        case 'hoverlay.core.Image.1.0':
          model = await HoverlayThreeUtils.createCoreImage(hobject, this.renderer, this.render)
          this.createMeshArray(node, model)
          modelGroup.add(model)
          break
        case 'hoverlay.core.Video.1.0':
        case 'hoverlay.core.ChromaVideo.1.0':
          model = await HoverlayThreeUtils.createCoreVideo(hobject, node, this.threeJSVideoComponents)
          this.createMeshArray(node, model)
          modelGroup.add(model)
          break
        case 'hoverlay.core.3dModel.1.0':
          model = await HoverlayThreeUtils.createCore3dModel(
            hobject,
            node,
            modelGroup,
            this.gltfLoader,
            this.mixerDictionnary
          )

          this.createMeshArray(node, model)
          modelGroup.add(model)
          break
        case 'hoverlay.core.Portal.1.0':
          console.log('createHobjectRepresentation')
          console.log(hobject)

          var res = await HoverlayThreeUtils.createCorePortalThumbnail(
            hobject,
            node,
            this.renderer,
            this.render,
            this.threeJSVideoComponents
          )

          model = res.model
          this.createMeshArray(node, model, false)
          modelGroup.add(model)
          break
        default:
          break
      }
      var transform = new THREE.Group()

      transform.add(modelGroup)
      transform.name = node.pid

      // Setup rotation
      if (
        isNaN(node.quaternion_x) ||
        isNaN(node.quaternion_y) ||
        isNaN(node.quaternion_z) ||
        isNaN(node.quaternion_w)
      ) {
        // Using euler
        transform.rotation
        new THREE.Euler(
          THREE.MathUtils.degToRad(-node.angle_x),
          THREE.MathUtils.degToRad(-node.angle_y),
          THREE.MathUtils.degToRad(node.angle_z),
          'ZXY'
        )
      } else {
        // Using quaternion
        var quaternion = new THREE.Quaternion(
          node.quaternion_x,
          node.quaternion_y,
          -node.quaternion_z,
          -node.quaternion_w
        )

        transform.quaternion.copy(quaternion) // Apply Quaternion
        transform.quaternion.normalize() // Normalize Quaternion
      }

      //Setup position
      transform.position.set(node.x, node.y, -node.z)

      //Set scale
      transform.scale.set(node.scale, node.scale, node.scale)

      this.scene.add(transform)
      this.render()

      this.transformDictionnary[node.pid] = transform
    },
    async createHobjectsRepresentation() {
      for (const node of this.nodes) {
        await this.createHobjectRepresentation(node)
        this.render()
      }
      this.renderer.domElement.addEventListener('mousedown', this.onDocumentMouseDown, false)
    },
    mouseDownHandler(transform, node) {
      // If this node is not present in the history yet then add it
      if (this.historyTransformArray.find(h => h.node.pid == node.pid) == undefined) {
        // Save rotation, position and rotation
        this.historyTransformArray.push({
          node: Object.assign({}, node),
          transform: transform,
          pos_x: parseFloat(transform.position.x.toFixed(3)),
          pos_y: parseFloat(transform.position.y.toFixed(3)),
          pos_z: parseFloat(transform.position.z.toFixed(3)),
          quaternion_x: parseFloat(transform.rotation.x.toFixed(3)),
          quaternion_y: parseFloat(transform.rotation.y.toFixed(3)),
          quaternion_z: parseFloat(transform.rotation.z.toFixed(3)),
          quaternion_w: transform.rotation.w ? parseFloat(transform.rotation.w.toFixed(3)) : undefined,
          scale: transform.scale.x,
        })
      }
    },
    addTransformControls(transform, node) {
      this.transformControls = new TransformControls(this.currentCamera, this.renderer.domElement)
      this.transformControls.space = 'world'
      this.transformControls.size = 1.4
      this.transformControls.addEventListener('change', this.render)
      // Disable hobject selection when a transformControls is active (avoid conflict event)
      this.transformControls.addEventListener('mouseDown', event => this.mouseDownHandler(transform, node))
      this.transformControls.addEventListener('objectChange', event => this.scaleChange(transform, node))
      this.transformControls.addEventListener('mouseUp', event => this.mouseUpHandler(transform, node, event))
      this.transformControls.customParamTransform = transform
      this.transformControls.customParamNode = node
      this.transformControls.addEventListener('dragging-changed', this.onDraggingChanged)

      var gizmoRotateX = this.transformControls._gizmo.gizmo['rotate'].children.find(mesh => mesh.name == 'X')
      gizmoRotateX.material.color.setHex(0xab3741)

      var gizmoRotateY = this.transformControls._gizmo.gizmo['rotate'].children.find(mesh => mesh.name == 'Y')
      gizmoRotateY.material.color.setHex(0x87d68a)

      var gizmoTranslateX = this.transformControls._gizmo.gizmo['translate'].children.find(mesh => mesh.name == 'X')
      gizmoTranslateX.material.color.setHex(0x87d68a)

      var gizmoTranslateY = this.transformControls._gizmo.gizmo['translate'].children.find(mesh => mesh.name == 'Y')
      gizmoTranslateY.material.color.setHex(0xab3741)

      var gizmoTranslateZ = this.transformControls._gizmo.gizmo['translate'].children.find(mesh => mesh.name == 'Z')
      gizmoTranslateZ.material.color.setHex(0x4185d0)

      // Snap/Magnetic
      this.transformControls.setTranslationSnap(0.01)

      this.transformControls.attach(transform)
      this.transformControlDictionnary[node.pid] = this.transformControls
      this.scene.add(this.transformControls)
      this.changeTransformControl(this.controlModeFloor)

      this.render()
    },
    addCompassImage() {
      var geometry, material, mesh
      var loader = new THREE.TextureLoader()
      var texture = loader.load(`${window.location.origin}/images/compass.png`)
      geometry = new THREE.PlaneBufferGeometry()
      material = new THREE.MeshBasicMaterial({ map: texture, opacity: 0.4, transparent: true })
      material.side = THREE.DoubleSide
      console.log('addCompassImage')
      // console.log(this.space.settings.heading)
      mesh = new THREE.Mesh(geometry, material)
      mesh.position.set(0, 0.01, 0)
      mesh.scale.set(5, 5, 5)
      mesh.rotation.set(-Math.PI / 2, 0, THREE.MathUtils.degToRad(this.space.settings.heading))
      this.scene.add(mesh)
    },
    addMannequin() {
      var context = this
      // Load a glTF resource
      this.gltfLoader.load(
        // resource URL
        `${window.location.origin}/models/mannequin.glb`,
        // called when the resource is loaded
        function(gltf) {
          // Rescale and place mannequin on the ground
          //gltf.scene.scale.set(150, 150, 150) // scale here
          var mroot = gltf.scene
          var bbox = new THREE.Box3().setFromObject(mroot)
          var cent = bbox.getCenter(new THREE.Vector3())
          var size = bbox.getSize(new THREE.Vector3())
          bbox.setFromObject(mroot)
          bbox.getCenter(cent)
          bbox.getSize(size)
          //Reposition to 0,halfY,0
          mroot.position.copy(cent).multiplyScalar(-1)
          mroot.position.y += size.y * 0.5

          context.mannequinObject = gltf.scene
          context.scene.add(gltf.scene)

          // Play animation
          context.mixerDictionnary['origin'] = new THREE.AnimationMixer(gltf.scene)
          var action = context.mixerDictionnary['origin'].clipAction(gltf.animations[0])
          action.clampWhenFinished = true
          action.loop = THREE.LoopOnce
          action.play()

          gltf.animations // Array<THREE.AnimationClip>
          gltf.scene // THREE.Group
          gltf.scenes // Array<THREE.Group>
          gltf.cameras // Array<THREE.Camera>
          gltf.asset // Object

          // gltf.scene.traverse(function(child) {
          //   if (child.isMesh) {
          //     child.material = new THREE.MeshBasicMaterial({
          //       color: 'red',
          //       transparent: true,
          //       opacity: 0.6,
          //     })
          //   }
          // })
          // Adjust model position
          gltf.scene.position.x = 0.07
          gltf.scene.position.z = 0.05

          gltf.scene.rotateY(-Math.PI)
          context.render()
        },
        // called while loading is progressing
        // eslint-disable-next-line no-unused-vars
        function(xhr) {
          // - // console.log((xhr.loaded / xhr.total) * 100 + '% loaded')
        },
        // called when loading has errors
        // eslint-disable-next-line no-unused-vars
        function(error) {
          // console.log(error)
          // console.log('An error happened')
        }
      )
    },
    scaleChange(group, node) {
      group.scale.x = group.scale.y = group.scale.z = Math.abs(group.scale.y)
    },
    mouseUpHandler(transform, node, event) {
      node = this.nodes.find(n => n.pid === node.pid)
      node.is_dirty = true
      node.scale = transform.scale.x
      if (!transform) return
      node.x = parseFloat(transform.position.x.toFixed(2))
      node.y = parseFloat(transform.position.y.toFixed(2))
      node.z = -parseFloat(transform.position.z.toFixed(2))
      node.angle_x = Math.round(parseFloat(THREE.MathUtils.radToDeg(-transform.rotation.x)))
      node.angle_y = Math.round(parseFloat(THREE.MathUtils.radToDeg(-transform.rotation.y)))
      node.angle_z = Math.round(parseFloat(THREE.MathUtils.radToDeg(-transform.rotation.z)))
      node.quaternion_x = transform.quaternion.x
      node.quaternion_y = transform.quaternion.y
      node.quaternion_z = -transform.quaternion.z
      node.quaternion_w = -transform.quaternion.w
      this.historyTransformArray.push({
        node: Object.assign({}, node),
        transform: transform,
        pos_x: parseFloat(transform.position.x.toFixed(3)),
        pos_y: parseFloat(transform.position.y.toFixed(3)),
        pos_z: parseFloat(transform.position.z.toFixed(3)),
        quaternion_x: parseFloat(transform.quaternion.x.toFixed(3)),
        quaternion_y: parseFloat(transform.quaternion.y.toFixed(3)),
        quaternion_z: parseFloat(transform.quaternion.z.toFixed(3)),
        quaternion_w: parseFloat(transform.quaternion.w.toFixed(3)),
        scale: parseFloat(transform.scale.x),
      })
      this.$emit('checkIfNodesAreDirties')
    },

    onWindowResize() {
      this.CANVAS_HEIGHT = document.getElementById('scene3d').offsetHeight
      this.CANVAS_WIDTH = document.getElementById('scene3d').offsetWidth
      const aspect = this.CANVAS_WIDTH / this.CANVAS_HEIGHT
      this.currentCamera.aspect = aspect
      this.currentCamera.updateProjectionMatrix()
      this.renderer.setSize(this.CANVAS_WIDTH, this.CANVAS_HEIGHT)
      this.render()
    },
    onDraggingChanged(event) {
      this.orbit.enabled = !event.value
    },
    onKeyUp(event) {
      switch (event.keyCode) {
        case 16: // Shift
          if (this.transformControls) {
            this.transformControls.setTranslationSnap(null)
            this.transformControls.setRotationSnap(null)
            this.transformControls.setScaleSnap(null)
          }
          break
      }
    },
    // event.ctrlKey && event.key === 'z'

    onKeyDown(event) {
      if (event.which === 89 && event.ctrlKey) {
        this.Repeat()
      } else if (event.which === 90 && event.ctrlKey) {
        this.Undo()
      } else if (event.which === 89 && event.metaKey) {
        this.Repeat()
      } else if (event.which === 90 && event.metaKey) {
        this.Undo()
      }

      switch (event.keyCode) {
        case 81: // Q
          // self.controls.forEach(control => {
          //   control.setSpace(control.space === 'local' ? 'world' : 'local')
          // })
          break
        case 16: // Shift
          if (this.transformControls) {
            this.transformControls.setTranslationSnap(0.1)
            this.transformControls.setRotationSnap(THREE.MathUtils.degToRad(5))
            this.transformControls.setScaleSnap(0.25)
          }
          break
        case 77: // M
          this.controlModeFloor = 'translate'
          break
        case 82: // R
          this.controlModeFloor = 'rotate'
          break
        case 83: // S
          this.controlModeFloor = 'scale'
          break
        case 86: // V - First-Person View
          this.isFirstPersonView = !this.isFirstPersonView
          break
        case 187:
        case 107: // +, =, num+
          this.control.setSize(this.control.size + 0.1)
          break
        case 189:
        case 109: // -, _, num-
          this.control.setSize(Math.max(this.control.size - 0.1, 0.1))
          break
      }
    },

    render() {
      if (this.threeJSVideoComponents)
        this.threeJSVideoComponents.forEach(el => {
          // - // console.log( el.video)

          if (el.video.readyState == el.video.HAVE_ENOUGH_DATA) {
            el.videoImageContext.drawImage(el.video, 0, 0)
            if (el.videoTexture) el.videoTexture.needsUpdate = true
          }
        })
      this.renderer.render(this.scene, this.currentCamera)
      if (this.captureThumbnail == true) {
        // console.log(this.renderer.domElement)
        // var strMime = 'image/jpeg'
        // this.space.thumbnail = this.renderer.domElement.toDataURL(strMime)
        // this.onThumbnailReady()
        // this.captureThumbnail = false
      }
    },
    onThumbnailReady() {},
    positionChanged() {},
    reset() {
      this.setupScene()
      this.addMannequin()
      if (this.space.settings && this.space.settings.compass_oriented) this.render()
    },
    getHobject(hobject_pid) {
      return JSON.parse(
        JSON.stringify(this.$store.state.hoverlay.hobjects.filter(hobject => hobject.pid == hobject_pid)[0])
      )
    },

    unregisterTransformControl() {
      this.transformControls && this.transformControls.removeEventListener('change', this.render)
      this.transformControls && this.transformControls.removeEventListener('mouseUp', this.onMouseUp)
      this.transformControls && this.transformControls.removeEventListener('mouseDown', this.mouseDownHandler)
      this.transformControls && this.transformControls.removeEventListener('mouseUp', this.mouseUpHandler)
      this.transformControls && this.transformControls.removeEventListener('objectChange', this.scaleChange)
      this.transformControls && this.transformControls.removeEventListener('dragging-changed', this.onDraggingChanged)
    },
    stopRendering() {
      cancelAnimationFrame(this.requestAnimationFrameId)
      this.renderer.setAnimationLoop(null) // pause the animation
    },
    radToDeg(radians) {
      var pi = Math.PI
      return radians * (180 / pi)
    },
    disposeScene() {
      this.unregisterTransformControl()

      cancelAnimationFrame(this.requestAnimationFrameId)
      this.renderer.setAnimationLoop(null) // pause the animation

      // Others events
      this.renderer.domElement.removeEventListener('click', this.onDocumentMouseDown, false)
      this.orbit.removeEventListener('change', this.render)
      window.removeEventListener('resize', this.onWindowResize)
      window.removeEventListener('keydown', this.onKeyDown)
      window.removeEventListener('keyup', this.onKeyUp)

      DestroyUtils.destroyThreejs({ scene: this.scene, renderer: this.renderer, camera: this.camera })
      // remove reference on global attributes
      // Three js variables
      this.cameraPersp = null
      this.currentCamera = null
      this.scene = null
      this.meshes = []
      this.renderer = null
      this.transformControlDictionnary = []
      this.transformDictionnary = []
      this.transformControls = null
      this.orbit = null
      this.CANVAS_HEIGHT = null
      this.CANVAS_WIDTH = null
      this.mannequinObject = null
      this.isFirstPersonView = false
      this.thirdPersonCoordinates = null
      this.thirdPersonOrbitTarget = null
      this.thirdPersonAngleX = null
      this.thirdPersonAngleY = null
      this.thirdPersonAngleZ = null
      //RAYCASTING
      this.raycaster = null
      this.mouse = null
      this.threeJSVideoComponents = []
      this.clock = null
      this.mixer = null
      this.mixerDictionnary = null
      return
    },
    animate() {
      const mixerUpdateDelta = this.clock.getDelta()
      if (this.mixerDictionnary)
        for (const mixer of Object.values(this.mixerDictionnary)) {
          mixer.update(mixerUpdateDelta)
        }
      this.requestAnimationFrameId = requestAnimationFrame(this.animate)
      TWEEN.update()
      this.render()
    },
    changeTransformControl(mode) {
      switch (mode) {
        case 'translate':
          for (const control of Object.values(this.transformControlDictionnary)) {
            control.setMode('translate')
            control.setSpace('world')
            control.showZ = true
            control.showX = true
            control.showY = true
          }
          break
        case 'scale':
          for (const control of Object.values(this.transformControlDictionnary)) {
            control.showZ = false
            control.showX = false
            control.showY = true
            control.setMode('scale')
            control.setSpace('world')
          }
          break
        case 'rotate':
          // Rotate
          for (const control of Object.values(this.transformControlDictionnary)) {
            control.showX = true
            control.showY = true
            control.showZ = true
            control.setMode('rotate')
            control.setSpace('local')
          }
          break
        default:
          break
      }
    },
    createMeshArray(node, model, castShadow = true) {
      var self = this
      model.traverse(function(child) {
        if (child.isMesh) {
          child.name = node.pid
          child.castShadow = castShadow
          self.meshes.push(child)
        }
      })
    },
    Repeat() {
      console.log('Repeat')
    },
    Undo() {
      // If there is no history do nothing
      if (this.historyTransformArrayIndex == 0) return
      if (this.historyTransformArrayIndex > 0) this.historyTransformArrayIndex -= 1
      var h = this.historyTransformArray[this.historyTransformArrayIndex]
      var node = this.nodes.find(n => n.pid == h.node.pid)

      // Undo transform on the threejs view
      h.transform.position.set(h.pos_x, h.pos_y, h.pos_z)
      h.transform.scale.set(h.scale, h.scale, h.scale)
      var quaternion = new THREE.Quaternion(h.quaternion_x, h.quaternion_y, h.quaternion_z, h.quaternion_w)
      h.transform.quaternion.copy(quaternion) // Apply Quaternion
      h.transform.quaternion.normalize() // Normalize Quaternion

      // Undo transform inside the
      node.x = h.pos_x
      node.y = h.pos_y
      node.z = -h.pos_z
      node.quaternion_x = h.quaternion_x
      node.quaternion_y = h.quaternion_y
      node.quaternion_z = -h.quaternion_z
      node.quaternion_w = -h.quaternion_w
      node.scale = h.scale

      // If we reach the begining of the history clean it
      if (this.historyTransformArrayIndex == 0) {
        this.historyTransformArray.splice(1)

        this.nodes.forEach(n => {
          console.log(n.was_saved)
          if (n.was_saved == true) {
            n.is_dirty = true
            n.was_saved = false
          } else n.is_dirty = false
        })
      } else {
        this.nodes.forEach(n => {
          n.is_dirty = true
        })
      }
    },
    onDocumentMouseDown(event) {
      if (this.transformControls != null && this.transformControls.axis != null) return

      var canvasPosition = this.renderer.domElement.getBoundingClientRect()
      var mouseX = event.clientX - canvasPosition.left
      var mouseY = event.clientY - canvasPosition.top
      var mouse = new THREE.Vector3(
        2 * (mouseX / this.renderer.domElement.clientWidth) - 1,
        1 - 2 * (mouseY / this.renderer.domElement.clientHeight),
        0.5
      )
      var raycaster = new THREE.Raycaster(
        this.currentCamera.position,
        mouse
          .unproject(this.currentCamera)
          .sub(this.currentCamera.position)
          .normalize()
      )

      var intersects = raycaster.intersectObjects(this.meshes, true)
      if (intersects.length > 0) {
        var nodePid = intersects[0].object.name
        var node = this.nodes.find(node => JSON.stringify(node.pid) == JSON.stringify(nodePid))
        if (!this.selectedNode || (node && node.pid != this.selectedNode.pid)) {
          if (this.transformControls != null) {
            this.transformControls.detach() // <- THIS IS CORRECT
            this.unregisterTransformControl()
            this.scene.remove(this.transformControls)
          }
          this.addTransformControls(this.transformDictionnary[nodePid], node)
          // console.log(node)
          this.$emit('selectedNode', node)
        }
      }
    },
    onTransformInputChange(value) {
      console.log(value)
    },
  },
}
</script>

<style lang="scss">
#div-with-loading {
  height: 50px;
  width: 50px;
}
#div-with-loading > .con-vs-loading {
  background-color: rgba(255, 255, 128, 0);
}
.center-top-right-loading {
  right: 0;
  text-align: right;
  position: absolute;
  top: 0;
}
.center-top-right {
  right: 0;
  width: 300px;
  text-align: right;
  position: absolute;
  top: 0;
  padding-right: 10px;
}

.center-top-left {
  width: 50px;
  text-align: left;
  position: absolute;
  padding-left: 10px;
  top: 0;
}

.pov {
  display: flex;
  gap: 5px;
}

.povBottomLeft {
  display: flex;
  padding-left: 10px;
  text-align: left;
  position: absolute;
  bottom: 5px;
  gap: 5px;
}

.povBottomRight {
  display: flex;
  padding-right: 10px;
  text-align: right;
  position: absolute;
  bottom: 5px;
  gap: 5px;
  right: 0;
}

.no-padding {
  padding: 0rem !important;
}

#scene3d {
  // height: 550px;
  // position: static;
  /* fixed or static */
  background: transparent;
  position: relative;
  z-index: 0;
}

.firstPersonView {
  display: flex;
  flex-direction: row;
  z-index: 1;
}

#iPhoneUi {
  position: absolute;
  z-index: 2;
  margin-left: auto;
  margin-right: auto;
  margin-top: auto;
  margin-bottom: auto;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  opacity: 0.8;
  pointer-events: none;
}

.grey {
  position: fixed;
  z-index: 2;
  background-color: #878787;
  opacity: 0.5;
  height: 400px;
  width: 650px;
  // -webkit-clip-path: polygon(16% 0%, 93% 0%, 75% 100%, 16% 100%);
  // clip-path: polygon(16% 0%, 93% 0%, 75% 100%, 16% 100%);
  // clip-path: require('../../../../../assets/images/hoverlay/iPhone-Ui.png');
  // margin-left: auto;
  // margin-right: auto;
  // margin-top: auto;
  // margin-bottom: auto;
  // left: 0;
  // right: 0;
  // top: 0;
  // bottom: 0;
}
</style>
